import './style.css';
import * as THREE from 'three';
import RAPIER from '@dimforge/rapier3d-compat';
import { View } from './render/scene';
import { cellCenter } from './world/city';
import { PhysicsCity } from './physics/world';
import { Input, type Action } from './input/actions';
import { Cameras } from './input/cameras';
import { AdaptiveQuality, PRESETS, resolveQuality, FIXED_DT, LIMITS } from './core/config';
import { loadPreferences, savePreferences } from './core/preferences';
import { FixedClock, Lifecycle } from './core/lifecycle';
import { Metrics } from './core/metrics';
import { Effects } from './render/effects';
import { AudioSystem, type Sound, type Soundscape } from './simulation/audio';
import { Tools } from './simulation/tools';
import { Population } from './simulation/population';
import { Rebuild } from './simulation/rebuild';
import { HUD } from './ui/hud';
import { FireSystem } from './simulation/fire';
import { Environment } from './simulation/environment';
import { GpuTimer } from './render/gpu-timer';
import { WaterSystem } from './simulation/water';
import { EntityRegistry } from './core/entities';
import { CivilLife } from './simulation/civil-life';
import { Services } from './simulation/services';
import { Railway } from './simulation/rail';
import { railStations } from './world/rail';
import { Aviation } from './simulation/aviation';
import { Airstrikes } from './simulation/airstrikes';
import { planeImpact } from './simulation/plane-impact';
import { Marine } from './simulation/marine';
import { AirSupport } from './simulation/air-support';
import { Buses } from './simulation/buses';
import { Logistics } from './simulation/logistics';
import { ShopStaff } from './simulation/shop-staff';
import { CoastStaff } from './simulation/coast-staff';
import { PowerPlant } from './simulation/power-plant';
import { Military } from './simulation/military';
import { Naval } from './simulation/naval';
import { Shelters } from './simulation/shelters';
import { CivilianDrivers } from './simulation/civilian-drivers';
import { Invasion } from './simulation/invasion';
import { createLivingCity } from './world/living-city';
import { IS_LITE, EDITION, GAME_NAME } from './core/edition';
document.title = GAME_NAME;
document.documentElement.dataset.edition = EDITION;
const startupBegin = performance.now();
class Game {
  readonly city = createLivingCity(73421, {
    towerTrial: true,
    strategicTrial: true,
    streetLife: true,
    lite: IS_LITE,
  });
  readonly life = new Lifecycle();
  readonly entities = new EntityRegistry(this.life);
  readonly clock = new FixedClock();
  readonly metrics = new Metrics();
  readonly adaptive = new AdaptiveQuality();
  readonly settings = loadPreferences(matchMedia('(prefers-reduced-motion: reduce)').matches);
  readonly view: View;
  readonly physics: PhysicsCity;
  readonly input: Input;
  readonly cameras: Cameras;
  readonly effects: Effects;
  readonly audio = new AudioSystem();
  readonly tools: Tools;
  readonly population: Population;
  readonly rebuild: Rebuild;
  readonly ui: HUD;
  readonly fire: FireSystem;
  readonly environment: Environment;
  readonly gpuTimer: GpuTimer;
  readonly water: WaterSystem;
  paused = false;
  hidden = false;
  lastTime = 0;
  refreshMs = 1000 / 60;
  uiTime = 0;
  streamTime = 0;
  resetCount = 0;
  activeWallSeconds = 0;
  frameDiscardedSeconds = 0;
  startupMs = 0;
  private audioScene: Soundscape[] = [];
  private audioSceneAge = 0;
  constructor() {
    this.view = new View(document.getElementById('app')!);
    this.view.scenery(this.city);
    this.gpuTimer = new GpuTimer(this.view.renderer.getContext() as WebGL2RenderingContext);
    this.physics = new PhysicsCity(this.view.scene, this.view.material, this.life);
    this.physics.profileEnabled = new URLSearchParams(location.search).has('metrics');
    this.physics.load(this.city);
    this.physics.entityRay = (origin, direction, length) => this.entities.raycast(origin, direction, length);
    this.physics.entityForBody = (body) => this.entities.forBody(body);
    this.input = new Input(this.view.canvas);
    this.cameras = new Cameras(this.view.camera, this.input, this.physics, this.settings);
    this.cameras.setRegion(this.city);
    this.cameras.reset();
    this.effects = new Effects(this.view.scene);
    this.tools = new Tools(this.physics, this.cameras, this.effects, this.audio, this.entities);
    this.fire = new FireSystem(this.view.scene);
    this.environment = new Environment(this.physics, this.fire, this.effects);
    this.environment.reset();
    this.water = new WaterSystem(this.physics, this.city);
    this.population = new Population(
      this.physics,
      this.city,
      this.fire,
      this.effects,
      this.audio,
      this.entities,
    );
    this.population.setWater(this.water);
    this.population.viewCamera = this.view.camera;
    this.population.viewLight = this.view.sun;
    this.population.reset(this.city.seed, this.settings.density);
    this.population.civil = new CivilLife(this.city, this.physics, this.population);
    this.population.services = new Services(this.city, this.physics, this.population, this.fire);
    if (!IS_LITE) this.population.rail = new Railway(this.city, this.physics, this.population);
    this.population.aviation = new Aviation(this.city, this.physics, this.population);
    this.population.airstrikes = new Airstrikes(this.population.aviation);
    this.population.aviation.cargoImpactEnabled = true;
    this.tools.planeRadius = planeImpact(65, true).radius;
    this.population.airSupport = new AirSupport(this.city, this.physics, this.population);
    if (!IS_LITE) this.population.buses = new Buses(this.city, this.physics, this.population);
    if (!IS_LITE) this.population.logistics = new Logistics(this.city, this.physics, this.population);
    if (!IS_LITE) this.population.shopStaff = new ShopStaff(this.city, this.physics, this.population);
    if (!IS_LITE) this.population.coastStaff = new CoastStaff(this.city, this.physics, this.population);
    if (this.city.strategic) {
      this.population.powerPlant = new PowerPlant(this.city, this.physics, this.population);
      this.population.naval = new Naval(this.city, this.physics, this.population);
      this.population.military = new Military(this.city, this.physics, this.population);
      this.population.drivers = new CivilianDrivers(this.physics, this.population);
      this.population.shelters = new Shelters(this.city, this.physics, this.population);
      this.population.invasion = new Invasion(this.city, this.physics, this.population);
    }
    if (!IS_LITE) this.population.marine = new Marine(this.city, this.physics, this.population);
    this.tools.onPlaneCrash = (point, direction) => this.population.aviation!.launchTest(point, direction);
    this.tools.onAirstrike = (point, direction) => this.population.airstrikes!.request(point, direction);
    this.tools.canAttach = (body) => this.population.canAttach(body);
    this.rebuild = new Rebuild(this.physics, this.city, this.life, this.audio);
    this.rebuild.focus = () => this.cameras.target;
    this.ui = new HUD(this.input, this.settings);
    if (this.population.invasion) {
      const invasion = this.population.invasion,
        dialog = this.ui.get<HTMLDialogElement>('invasion-confirm');
      this.ui.get('invasion-start').hidden = false;
      this.ui.get('invasion-start').onclick = () => {
        if (invasion.active || this.life.phase !== 'playing') return;
        this.input.cancel();
        this.input.enabled = false;
        dialog.showModal();
      };
      dialog.addEventListener('close', () => {
        this.input.cancel();
        this.input.enabled = !this.paused;
      });
      this.ui.get('invasion-confirm-back').onclick = () => dialog.close();
      this.ui.get('invasion-confirm-start').onclick = () => {
        invasion.start();
        dialog.close();
      };
      for (const id of ['invasion-cancel', 'invasion-settings-cancel'])
        this.ui.get(id).onclick = () => invasion.cancel();
      invasion.onMessage = (text) => this.ui.message(text);
    }
    if (this.population.military) {
      this.population.military.security.onWarning = (text) => this.ui.message(text);
      this.population.military.relief.onDispatch = (text) => this.ui.message(text);
    }
    this.ui.get('audio-listen').onclick = () => {
      void this.audio.audition(this.ui.get<HTMLSelectElement>('audio-sample').value as Sound);
    };
    this.ui.get('audio-next').onclick = () => {
      void this.audio.skip();
    };
    this.ui.get('audio-record').onclick = () => {
      this.pause(false);
      void this.audio.record(20, (blob) => {
        const url = URL.createObjectURL(blob),
          a = document.createElement('a');
        a.href = url;
        a.download = 'BlockSandbox-gameplay-mix.wav';
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        this.ui.message('Gameplay mix recorded');
      });
      this.ui.message('Recording the next 20 seconds of game audio');
    };
    const visit = this.ui.get<HTMLSelectElement>('district-inspect');
    for (const district of this.city.districts ?? [])
      visit.add(new Option(district.name, `district:${district.id}`));
    for (const [index] of (this.city.strategic?.transport?.landings ?? []).entries())
      visit.add(new Option(index === 0 ? 'Base naval pier' : `Coastal landing ${index}`, `landing:${index}`));
    for (const edge of this.city.roadGraph!.edges.filter((e) => e.curved && e.bridgeId !== undefined))
      visit.add(new Option(`Riverbank crossing ${edge.bridgeZ}`, `bridge:${edge.id}`));
    visit.onchange = () => {
      const [kind, id] = visit.value.split(':');
      const district = this.city.districts?.find((d) => d.id === id);
      const bridge = this.city.bridges?.find(
        (b) => b.id === this.city.roadGraph!.edges.find((e) => e.id === Number(id))?.bridgeId,
      );
      const landing = kind === 'landing' ? this.city.strategic?.transport?.landings[Number(id)] : undefined;
      const p =
        landing?.berth ??
        (kind === 'bridge' && bridge ? { x: bridge.x + bridge.w * 0.625, z: bridge.z } : district);
      if (!p) return;
      for (const selector of ['aviation-inspect', 'bus-inspect', 'delivery-inspect'])
        this.ui.get<HTMLSelectElement>(selector).value = 'off';
      this.cameras.target.set(p.x, kind === 'bridge' ? 4 : 0, p.z);
      this.cameras.distance = landing ? 130 : kind === 'bridge' ? 125 : 230;
      this.cameras.pitch = 0.9;
    };
    const peek = this.ui.get<HTMLSelectElement>('interior-peek');
    const interiorView = this.ui.get<HTMLSelectElement>('interior-view');
    if (this.city.streetLife) {
      for (const id of ['interior-peek', 'interior-view', 'resident-inspect'])
        this.ui.get(id).closest('label')?.setAttribute('hidden', '');
    }
    for (const room of this.city.interiors ?? [])
      for (const floor of room.floors)
        peek.add(
          new Option(
            `${this.city.buildings.find((b) => b.id === room.buildingId)!.name} · floor ${floor + 1}`,
            `${room.buildingId}:${floor}`,
          ),
        );
    const applyPeek = () => {
      const [building, floor] = peek.value.split(':').map(Number);
      const glass = interiorView.value === 'glass';
      this.physics.setCutaway(peek.value === 'off' ? undefined : building, floor, glass);
      this.population.cutaway = peek.value === 'off' || glass ? undefined : { building, floor };
    };
    for (const facility of this.city.facilities ?? [])
      peek.add(
        new Option(
          `${this.city.buildings.find((b) => b.id === facility.buildingId)!.name} · inside`,
          `${facility.buildingId}:0`,
        ),
      );
    const inspectInterior = () => {
      applyPeek();
      if (peek.value !== 'off') {
        const [id, floor] = peek.value.split(':').map(Number),
          b = this.city.buildings.find((b) => b.id === id)!,
          room = this.city.interiors!.find((r) => r.buildingId === id);
        this.cameras.target.set(
          b.x + b.w * 0.625,
          room ? 1.41 + floor * room.floorHeight * 1.25 : 0.36,
          b.z + b.d * 0.625,
        );
        this.cameras.distance = Math.max(b.w, b.d) * 1.8;
        this.cameras.pitch = 1.08;
        if (interiorView.value === 'glass' && room) {
          this.cameras.target.z = b.z + 2.75;
          this.cameras.target.y += 0.55;
          this.cameras.distance = Math.min(28, Math.max(12, b.w * 1.15));
          this.cameras.yaw = Math.PI;
          this.cameras.pitch = 0.18;
        }
      }
    };
    peek.onchange = inspectInterior;
    interiorView.onchange = inspectInterior;
    const inspect = this.ui.get<HTMLSelectElement>('resident-inspect');
    for (const r of this.population.civil?.routines.values() ?? [])
      inspect.add(new Option(`${r.person.name} · ${r.role}`, r.person.id));
    inspect.onchange = () => {
      const info = this.ui.get('resident-info'),
        r = this.population.civil?.inspect(inspect.value)[0];
      info.hidden = !r;
      if (r) {
        const person = this.population.people.find((p) => p.id === inspect.value);
        if (person) {
          this.cameras.target.copy(person.p);
          this.cameras.distance = 28;
          this.cameras.pitch = 0.82;
        }
        info.textContent = [
          r.intent,
          r.reason,
          `Layer: ${r.layer} · Support: ${r.support ?? 'none'}`,
          `Completed: ${r.completed} · Replans: ${r.replans} · Carrying: ${r.carrying || 'nothing'}`,
        ].join('\n');
      }
    };
    this.input.onGesture = () => this.audio.start();
    if (IS_LITE) {
      for (const id of ['rail-inspect', 'bus-inspect', 'delivery-inspect', 'railHeadway', 'aviation-inspect'])
        this.ui.get(id).closest('label')?.setAttribute('hidden', '');
    }
    const railInspect = this.ui.get<HTMLSelectElement>('rail-inspect');
    for (const station of railStations(this.city.rail)) railInspect.add(new Option(station.name, station.id));
    railInspect.onchange = () => {
      const station = railStations(this.city.rail).find((s) => s.id === railInspect.value);
      if (!station) return;
      this.cameras.target.set(station.point.x, station.lift.y, station.point.z);
      this.cameras.distance = 62;
      this.cameras.pitch = 0.82;
      const railway = this.population.rail!,
        service = railway.layouts.find((line) => line.stations.some((s) => s.id === station.id))!,
        trains = railway.trains.filter((t) => t.layout === service);
      this.ui.get('rail-info').textContent =
        `${service.name} · ${trains.length} trains · ${railway.stationOpen(station) ? 'Platform open' : 'Service disrupted'}`;
    };
    this.input.onAction = this.ui.onAction = (a) => this.action(a);
    const busInspect = this.ui.get<HTMLSelectElement>('bus-inspect');
    for (const stop of this.city.streetTransit?.stops ?? []) busInspect.add(new Option(stop.name, stop.id));
    busInspect.onchange = () => {
      const stop = this.city.streetTransit?.stops.find((s) => s.id === busInspect.value);
      const point =
        stop?.point ?? (busInspect.value === 'follow' ? this.population.buses?.fleet[0]?.car.p : undefined);
      if (!point) return;
      this.cameras.target.set(point.x, point.y, point.z);
      this.cameras.distance = 42;
      this.cameras.pitch = 0.82;
      this.ui.get('bus-info').textContent =
        `${this.population.buses?.fleet.length ?? 0} local buses · eight pavement stops · boarding preserves each resident's journey`;
    };
    const deliveryInspect = this.ui.get<HTMLSelectElement>('delivery-inspect');
    for (const u of this.population.logistics?.fleet ?? [])
      deliveryInspect.add(new Option(u.person.name, u.person.id));
    deliveryInspect.onchange = () => {
      const u = this.population.logistics?.fleet.find((u) => u.person.id === deliveryInspect.value);
      if (!u) return;
      this.ui.get<HTMLSelectElement>('bus-inspect').value = 'off';
      this.ui.get<HTMLSelectElement>('aviation-inspect').value = 'off';
      this.cameras.target.copy(u.person.p);
      this.cameras.distance = 30;
      this.cameras.pitch = 0.86;
      this.ui.get('delivery-info').textContent =
        `${u.reason} · ${u.cargo} items aboard · ${u.delivered} delivered`;
    };
    if (this.city.strategic) {
      this.ui
        .get<HTMLSelectElement>('aviation-inspect')
        .add(new Option('Follow base air service', 'military-flight'));
      this.ui
        .get<HTMLSelectElement>('aviation-inspect')
        .add(new Option('Follow Blackguard air team', 'elite-flight'));
      this.ui
        .get<HTMLSelectElement>('aviation-inspect')
        .add(new Option('Follow hostile air transport', 'hostile-flight'));
      this.ui
        .get<HTMLSelectElement>('aviation-inspect')
        .add(new Option('Follow parachute descent', 'parachute'));
    }
    this.ui.get<HTMLSelectElement>('aviation-inspect').onchange = () => {
      const selected = this.ui.get<HTMLSelectElement>('aviation-inspect').value;
      const airport = this.city.airport!;
      if (selected === 'off') return;
      const descent =
        selected === 'parachute'
          ? this.population.military?.air.parachutes?.descents.values().next().value
          : undefined;
      if (selected === 'parachute' && !descent) {
        this.ui.get('aviation-info').textContent = 'No original passenger is currently descending';
        return;
      }
      const militaryFlight = this.population.aviation?.aircraft.find(
        (a) =>
          a.state !== 'wreck' &&
          (selected !== 'elite-flight' || a.mission?.owner.includes(':elite:')) &&
          a.mission?.faction ===
            (selected === 'military-flight' || selected === 'elite-flight'
              ? 'friendly'
              : selected === 'hostile-flight'
                ? 'hostile'
                : 'none'),
      );
      if (['military-flight', 'hostile-flight', 'elite-flight'].includes(selected) && !militaryFlight) {
        this.ui.get('aviation-info').textContent = 'No active transport for this service';
        return;
      }
      const airUnit = this.population.airSupport?.units.find((u) => `air-${u.pad.role}` === selected);
      const point =
        descent?.soldier.person.p ??
        militaryFlight?.p ??
        airUnit?.p ??
        (selected === 'terminal'
          ? { x: 390, y: 3, z: -380 }
          : selected === 'apron'
            ? { x: 530, y: 2, z: -425 }
            : selected === 'runway'
              ? { x: 765, y: 1, z: -550 }
              : this.population.aviation!.aircraft[0].p);
      this.cameras.target.set(point.x, point.y, point.z);
      this.cameras.distance = descent
        ? 35
        : militaryFlight
          ? 85
          : selected === 'terminal'
            ? 85
            : selected === 'runway'
              ? 450
              : 155;
      this.cameras.pitch = 0.9;
      this.ui.get('aviation-info').textContent = descent
        ? 'Original passenger descending to a supported landing'
        : militaryFlight
          ? militaryFlight.reason
          : airUnit
            ? airUnit.reason
            : `${airport.name} · two scheduled coastal excursions · ${this.population.aviation!.runwayOpen() ? 'runway open' : 'runway closed'}`;
    };
    this.input.onTool = this.ui.onTool = (t) => {
      this.tools.select(t);
      this.ui.selectTool(t);
    };
    this.tools.onMessage = (t) => this.ui.message(t);
    this.audio.onUnavailable = () => this.ui.audioAvailable(false);
    this.physics.onParticles = (p, c, n, s) => this.effects.burst(p, c, n, s);
    this.physics.onRubble = (power, point) => this.audio.play('rubble', power, point);
    this.physics.onDisturbance = (e) => {
      this.population.disturb(e);
      if (e.kind === 'collapse') this.audio.play('crack', 0.8, e.point);
    };
    this.physics.onBlast = (e) => {
      if (!this.life.valid(e.epoch)) return;
      this.environment.blast(e);
      this.population.damage(e);
      this.fire.refreshPositions();
      this.fire.heat(e);
    };
    this.physics.onContact = (body, point, energy) => this.population.contact(body, point, energy);
    this.physics.onChanged = (id) => {
      this.population.network.invalidate(id);
      this.population.civil?.network.invalidate(id);
      this.population.services?.invalidate(id);
      this.population.rail?.invalidate(id);
    };
    this.ui.onSettings = () => {
      this.configure();
      savePreferences(this.settings);
    };
    this.ui.onSkip = () => this.rebuild.finish();
    this.rebuild.onClean = () => this.cleanWorld();
    this.rebuild.onPrepare = (budgetMs) => {
      const deadline = performance.now() + budgetMs;
      while (this.restoring && performance.now() < deadline) {
        const r = this.physics.reconstruction;
        if (r && r.phase > this.physics.reconstructionStageLimit) break;
        if (this.restoring.next().done) this.restoring = undefined;
      }
      this.physics.commitReconstruction();
      return !this.restoring;
    };
    this.rebuild.onFinish = () => {
      this.population.show(true);
      this.ui.rebuild(null);
      this.clock.clear();
      this.resetCount++;
      this.configure();
      applyPeek();
    };
    this.configure();
    this.installEvents();
    this.view.resize();
    this.cameras.update(0, 1);
    // Finish the cold visible-scene upload while the loading screen is still
    // present. Startup latency is reported separately; play starts afterward.
    this.physics.sync(1);
    this.population.render(1);
    this.view.update(this.cameras.target, 0);
    this.view.render();
    this.view.renderer.getContext().finish();
    this.startupMs = performance.now() - startupBegin;
    document.getElementById('loading')?.remove();
    if (import.meta.env.DEV) this.debug();
    if (new URLSearchParams(location.search).has('metrics'))
      Object.assign(window, { __sandboxMetrics: this.readonlyMetrics() });
    requestAnimationFrame((t) => this.frame(t));
  }
  configure(): void {
    this.tools.c4Footprint = this.settings.c4Footprint;
    this.tools.c4Strength = this.settings.c4Strength;
    if (this.population.airstrikes) this.population.airstrikes.strength = this.settings.airstrikeStrength;
    if (this.population.rail) this.population.rail.headway = this.settings.railHeadway;
    const preset = resolveQuality(this.settings.quality, false);
    this.physics.preset = preset;
    this.physics.fragmentScale = this.settings.detail === 0 ? 2 : this.settings.detail === 2 ? 0.75 : 1;
    this.physics.detailDistance = this.settings.viewDistance;
    this.view.configure(this.settings, this.settings.adaptive ? this.adaptive.factor : 1);
    this.effects.limit = Math.min(
      480,
      Math.round(
        PRESETS[preset].particles *
          (this.settings.effects === 0 ? 0.4 : this.settings.effects === 2 ? 1 : 0.7),
      ),
    );
    this.effects.reduced = this.settings.reduced;
    this.audio.volume = this.settings.volume;
    this.audio.effectsVolume = this.settings.effectsVolume;
    this.audio.musicVolume = this.settings.musicVolume;
    this.audio.ambienceVolume = this.settings.ambienceVolume;
    this.audio.sirenVolume = this.settings.sirenVolume;
    this.audio.sirenMuted = this.settings.sirenMuted;
    this.audio.apply();
    this.ui.audioAvailable(this.audio.supported);
    this.fire.effectDetail = this.settings.effects;
    this.fire.reduced = this.settings.reduced;
    if (this.life.phase === 'playing' && this.population.density !== this.settings.density)
      this.population.setDensity(this.settings.density);
    this.ui.get('limits').textContent =
      `Up to ${LIMITS.charges} placed charges, with unlimited inventory across detonations. Your settings are kept when the city is rebuilt.`;
  }
  action(a: Action): void {
    if (a === 'pause') {
      this.pause(!this.paused);
      return;
    }
    if (a === 'debug') {
      this.ui.toggleDebug();
      return;
    }
    if (a === 'useEnd') {
      this.tools.cancelHold();
      return;
    }
    if (a === 'instant') {
      this.startRebuild(true);
      return;
    }
    if (this.paused) return;
    if (a === 'overview') {
      for (const selector of ['aviation-inspect', 'bus-inspect', 'delivery-inspect'])
        this.ui.get<HTMLSelectElement>(selector).value = 'off';
      this.cameras.overview();
      return;
    }
    if (a === 'rebuild') {
      this.startRebuild(false);
      return;
    }
    if (this.life.phase !== 'playing') return;
    if (a === 'useStart') this.tools.start();
    if (a === 'use') this.tools.use();
    if (a === 'detonate') this.tools.detonate();
  }
  pause(paused: boolean): void {
    this.paused = paused;
    this.input.cancel();
    this.input.enabled = !paused;
    this.clock.clear();
    this.lastTime = 0;

    this.audio.pause(paused || this.hidden);
    this.ui.pause(paused);
  }
  startRebuild(instant: boolean): void {
    if (this.life.phase === 'rebuilding') {
      if (instant) this.rebuild.finish();
      return;
    }
    this.input.cancel();
    this.population.invasion?.cancel('Rebuild requested');
    this.population.show(false);
    this.rebuild.begin();
    this.clock.clear();
    this.ui.rebuild(0);
    if (instant) this.rebuild.finish();
  }
  private restoring?: Generator<void>;
  private restorationWork: {
    stage: string;
    ms: number;
    phase?: number;
    building?: number;
    operation?: string;
  }[] = [];
  cleanWorld(): void {
    this.restorationWork = [];
    this.restoring = this.restoreWorld();
  }
  private *restoreSteps(stage: string, steps: Generator<void>): Generator<void> {
    for (;;) {
      const start = performance.now(),
        result = steps.next(),
        ms = performance.now() - start;
      if (ms > 6) {
        this.restorationWork.push({
          stage,
          ms,
          phase: this.physics.reconstruction?.phase,
          ...(stage === 'structural restoration' ? this.physics.reconstruction?.preparing : undefined),
        });
        this.restorationWork.sort((a, b) => b.ms - a.ms);
        this.restorationWork.length = Math.min(48, this.restorationWork.length);
      }
      if (result.done) return;
      yield;
    }
  }
  private *restoreWorld(): Generator<void> {
    this.tools.clear();
    this.audio.clear();
    this.water.clear();
    this.fire.clear();
    this.environment.clear();
    this.physics.jobs.clear();
    yield;
    yield* this.restoreSteps('clearing original activity', this.population.clearSteps());
    this.entities.clear();
    yield;
    yield* this.restoreSteps(
      'structural restoration',
      this.physics.loadSteps(this.city, this.cameras.target, this.settings.physicsRadius, true, true),
    );
    this.water.clear();
    yield* this.restoreSteps('fire and environment', this.environment.resetSteps());
    yield* this.restoreSteps(
      'occupants and transport',
      this.population.resetSteps(this.city.seed, this.settings.density, true),
    );
    this.population.show(false);
    this.configure();
  }
  installEvents(): void {
    window.addEventListener('resize', () => {
      this.view.resize();
      this.input.cancel();
    });
    window.addEventListener('blur', () => {
      this.input.cancel();
      this.clock.clear();
      this.audio.pause(true);
    });
    window.addEventListener('focus', () => {
      this.lastTime = 0;

      this.clock.clear();
      this.audio.pause(this.paused || this.hidden);
    });
    document.addEventListener('visibilitychange', () => {
      this.hidden = document.hidden;
      this.input.cancel();
      this.clock.clear();
      this.lastTime = 0;

      this.audio.pause(this.paused || this.hidden);
    });
    this.view.canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      this.view.lost = true;
      this.pause(true);
      this.ui.message('Graphics paused while the context recovers.');
    });
    this.view.canvas.addEventListener('webglcontextrestored', () => {
      this.view.lost = false;
      this.configure();
      this.ui.message('Graphics restored. Resume when ready.');
    });
  }
  frame(time: number): void {
    requestAnimationFrame((t) => this.frame(t));
    if (this.hidden || this.view.lost) {
      this.lastTime = 0;

      return;
    }
    if (!this.lastTime) this.lastTime = time;
    const cpu = performance.now(),
      raw = (time - this.lastTime) / 1000,
      dt = Math.min(raw, 0.067);
    this.lastTime = time;
    if (raw > 0.003 && raw < 0.04) this.refreshMs = Math.min(this.refreshMs, raw * 1000);
    let alpha = 1,
      physicsMs = 0;
    this.physics.structuralMs = 0;
    if (!this.paused) {
      if (this.life.phase === 'rebuilding') {
        // Rebuilding is an explicit wall-time animation, not a physics step.
        // Clamping it to 67 ms made slow compatibility renderers stretch a
        // seven-second reconstruction while also starving its loader.
        this.rebuild.update(raw);
        this.ui.rebuild(this.life.phase === 'rebuilding' ? this.rebuild.progress : null);
        if (this.life.phase === 'rebuilding') this.ui.get('rebuild-status').textContent = this.rebuild.status;
      } else {
        this.activeWallSeconds += raw;
        this.environment.flush();
        this.physics.process();
        this.population.civil?.beginFrame();
        this.population.services?.beginFrame();
        alpha = this.clock.advance(raw * (this.settings.slow ? 1 / 3 : 1), () => {
          this.tools.step(FIXED_DT);
          this.water.step(FIXED_DT);
          this.physics.step();
          physicsMs += this.physics.physicsMs;
          this.population.update(FIXED_DT);
          this.fire.update(FIXED_DT);
        });
        this.effects.update(dt * (this.settings.slow ? 1 / 3 : 1));
      }
    }
    this.physics.sync(alpha);
    // Deliberate navigation releases inspection tracking immediately. Otherwise
    // a following vehicle rewrites the user's pan/zoom focus every frame.
    if (
      this.input.zoom !== 0 ||
      this.input.pan.lengthSq() > 0 ||
      this.input.orbit.lengthSq() > 0 ||
      this.input.moveVector().lengthSq() > 0
    ) {
      for (const selector of ['aviation-inspect', 'bus-inspect', 'delivery-inspect'])
        this.ui.get<HTMLSelectElement>(selector).value = 'off';
    }
    if (!this.paused && this.ui.get<HTMLSelectElement>('aviation-inspect').value === 'flight') {
      const flight = this.population.aviation?.aircraft.find((a) => !a.test && a.state !== 'wreck');
      if (flight) this.cameras.target.copy(flight.p);
    }
    if (!this.paused && this.ui.get<HTMLSelectElement>('bus-inspect').value === 'follow') {
      const bus = this.population.buses?.fleet.find((b) => b.state !== 'wreck');
      if (bus) this.cameras.target.copy(bus.car.p);
    }
    if (!this.paused) {
      const selected = this.ui.get<HTMLSelectElement>('aviation-inspect').value;
      if (selected === 'parachute') {
        const descent = this.population.military?.air.parachutes?.descents.values().next().value;
        if (descent) this.cameras.target.copy(descent.soldier.person.p);
      }
      if (['military-flight', 'hostile-flight', 'elite-flight'].includes(selected)) {
        const flight = this.population.aviation?.aircraft.find(
          (a) =>
            a.state !== 'wreck' &&
            (selected !== 'elite-flight' || a.mission?.owner.includes(':elite:')) &&
            a.mission?.faction === (selected === 'hostile-flight' ? 'hostile' : 'friendly'),
        );
        if (flight) {
          this.cameras.target.copy(flight.p);
          this.ui.get('aviation-info').textContent = flight.reason;
        }
      }
      const unit = this.population.airSupport?.units.find((u) => `air-${u.pad.role}` === selected);
      if (unit) {
        this.cameras.target.copy(unit.p);
        this.ui.get('aviation-info').textContent = unit.reason;
      }
    }
    if (!this.paused) {
      const u = this.population.logistics?.fleet.find(
        (u) => u.person.id === this.ui.get<HTMLSelectElement>('delivery-inspect').value,
      );
      if (u) {
        this.cameras.target.copy(u.person.p);
        this.ui.get('delivery-info').textContent =
          `${u.reason} · ${u.cargo} items aboard · ${u.delivered} delivered`;
      }
    }
    this.cameras.update(this.paused ? 0 : raw, alpha);
    this.population.viewFocus = this.cameras.target;
    this.population.presentationHeight = this.view.canvas.height;
    this.population.render(alpha);
    this.tools.update(this.paused ? 0 : dt, alpha, !this.paused && this.life.phase === 'playing');
    this.view.update(this.cameras.target, this.physics.time);
    this.audioSceneAge += dt;
    if (this.audioSceneAge >= 0.35) {
      this.audioSceneAge = 0;
      this.audioScene = this.soundscape();
    }
    this.audio.update(dt, this.cameras.target, this.audioScene);
    const renderStart = performance.now();
    this.gpuTimer.begin();
    this.view.render();
    this.gpuTimer.end();
    const renderMs = performance.now() - renderStart;
    if (!this.paused && raw > 0) {
      if (this.settings.adaptive && this.adaptive.sample(raw * 1000, 1000 / this.refreshMs))
        this.view.configure(this.settings, this.adaptive.factor);
    }
    this.uiTime += dt;
    this.streamTime += dt;
    if (this.streamTime > 0.6 && this.life.phase === 'playing') {
      this.streamTime = 0;
      this.physics.stream(this.cameras.target, this.settings.physicsRadius);
    }
    if (this.uiTime > 0.2) {
      this.uiTime = 0;
      if (this.life.phase === 'playing') this.physics.updateDetail(this.view.camera.position);
      const s = this.snapshot();
      const sound = this.audio.inspect();
      this.ui.get('audio-track').textContent =
        `${sound.currentTrack ?? 'Soundtrack ready'} · ${Math.floor(Number(sound.trackSeconds))} s · Scott Buckley · ${sound.limiter ? 'output limiter active' : 'waiting for audio gesture'}`;
      this.ui.update(this.tools.charges.length, this.tools.hold, Number(s.fps), s);
      const invasion = this.population.invasion;
      if (invasion) {
        this.ui.get('invasion-start').hidden = invasion.active;
        for (const id of ['invasion-cancel', 'invasion-settings-cancel'])
          this.ui.get(id).hidden = !invasion.active;
        const status = this.ui.get('invasion-status');
        status.hidden = invasion.phase === 'idle' || this.life.phase !== 'playing';
        status.textContent = invasion.active
          ? `${invasion.phase === 'preparing' ? 'Preparing' : invasion.phase === 'deploying' ? 'Deploying' : 'Invasion'} · Friendly ${s.friendlyCombatCapable} · Hostile ${Number(s.hostileCombatCapable) + Number(s.hostileReserved)} · ${s.hostileAboard} aboard / ${s.hostileAirborne} descending · City loss ${Number(s.cityDestroyedPercent).toFixed(1)}%`
          : invasion.result;
        status.title = `${s.hostileCreated}/600 original hostiles created; ${s.hostileReserved} remaining transport assignments, ${s.hostileUnavailable} unavailable, ${s.hostileIncapacitated} incapacitated, ${s.hostileWithdrawn} withdrawn. ${invasion.reason}`;
      }
      const power = this.ui.get('city-power');
      power.hidden = !this.population.powerPlant;
      power.textContent = this.population.powerPlant ? `City power: ${this.population.powerPlant.state}` : '';
      power.title = this.population.powerPlant?.reason ?? '';
    }
    if (!this.paused && raw > 0)
      this.metrics.sample(
        raw * 1000,
        physicsMs,
        this.physics.structuralMs,
        renderMs,
        performance.now() - cpu,
        this.gpuTimer.sample,
      );
  }
  private soundscape(): Soundscape[] {
    const result: Soundscape[] = [],
      focus = this.cameras.target;
    const candidates = new Map<string, Soundscape>();
    const offer = (key: string, s: Soundscape) => {
      const old = candidates.get(key);
      if (
        !old ||
        Math.hypot(s.point.x - focus.x, s.point.z - focus.z) <
          Math.hypot(old.point.x - focus.x, old.point.z - focus.z)
      )
        candidates.set(key, s);
    };
    const plant = this.population.powerPlant;
    if (plant && plant.time < plant.alarmUntil)
      offer('siren', {
        id: 'plant-alarm',
        sample: 'siren',
        gain: 0.18,
        rate: 0.7,
        point: this.city.strategic!.parcels[1].gate,
      });
    for (const car of this.population.cars) {
      if (['wreck', 'impounded', 'towed', 'station', 'parked', 'deployed'].includes(car.state)) continue;
      if (car.incident)
        offer('siren', {
          id: 'near-siren',
          sample: 'siren',
          gain: 0.3,
          point: car.p,
          rate: car.kind === 'police' ? 1.07 : 0.95,
        });
      if (car.actualSpeed > 0.3)
        offer(car.kind === 'train' ? 'train' : 'traffic', {
          id: car.kind === 'train' ? 'near-train' : 'near-traffic',
          sample: 'engine',
          gain: car.kind === 'train' ? 0.19 : 0.12,
          point: car.p,
          rate:
            car.kind === 'train'
              ? 0.65
              : car.kind === 'motorcycle'
                ? 1.35
                : 0.75 + Math.min(0.5, car.actualSpeed * 0.05),
        });
    }
    for (const incident of this.fire.incidents())
      offer('fire', {
        id: 'near-fire',
        sample: 'fire',
        gain: Math.min(0.5, incident.intensity * 0.18),
        point: incident.point,
      });
    for (const a of this.population.aviation?.aircraft ?? [])
      if (!['parked', 'wreck', 'turnaround'].includes(a.state))
        offer('aircraft', { id: 'near-aircraft', sample: 'aircraft', gain: 0.35, point: a.p });
    for (const a of this.population.airSupport?.units ?? [])
      if (!['ready', 'wreck', 'unavailable', 'turnaround'].includes(a.state))
        offer('helicopter', { id: 'near-helicopter', sample: 'helicopter', gain: 0.4, point: a.p });
    const river = (this.city.river?.points ?? []).reduce<{ x: number; z: number } | undefined>(
      (best, p) =>
        !best || Math.hypot(p.x - focus.x, p.z - focus.z) < Math.hypot(best.x - focus.x, best.z - focus.z)
          ? p
          : best,
      undefined,
    );
    if (river) offer('water', { id: 'near-water', sample: 'waves', gain: 0.1, point: { ...river, y: 0 } });
    for (const animal of this.population.animals)
      if (animal.kind === 'bird' && !['incapacitated', 'falling'].includes(animal.state))
        offer('birds', { id: 'near-birds', sample: 'morning', gain: 0.06, point: animal.p });
    result.push(...candidates.values());
    return result;
  }
  snapshot(): Record<string, number | string> {
    return {
      physicsBackend: 'Rapier 0.20.0 SIMD (local package alias)',
      startupMs: this.startupMs,
      ...this.metrics.summary(),
      ...this.fire.stats,
      ...this.water.snapshot(),
      ...this.population.snapshot(),
      ...this.physics.bodyCounts,
      ...this.physics.batches.stats,
      resetTemplateBytes: this.physics.resetTemplateBytes,
      reconstructionPhase: this.physics.reconstruction?.phase ?? -1,
      reconstructionCells: this.physics.reconstruction?.cells ?? 0,
      reconstructionTotal: this.physics.reconstruction?.totalCells ?? 0,
      reconstructionMaxSliceMs: this.rebuild.profile.maxSliceMs,
      simulationSeconds: this.physics.time,
      activeWallSeconds: this.activeWallSeconds,
      discardedSimulationSeconds: this.frameDiscardedSeconds + this.clock.discardedSeconds,
      fixedSteps: this.clock.totalSteps,
      lastFixedSteps: this.clock.lastSteps,
      simulationBacklogSeconds: this.clock.accumulator,
      contactForceEvents: this.physics.contactEvents,
      totalContactForceEvents: this.physics.totalContactEvents,
      queueRejected: this.physics.jobs.rejected,
      queueCompleted: this.physics.jobs.completed,
      queueOldestMs: this.physics.jobs.oldestMs,
      queueLongestJobMs: this.physics.jobs.longestJobMs,
      pendingDamageCells: this.physics.damageInbox.size,
      pendingDamageOwners: this.physics.damageInbox.ownerCount,
      pendingHeatOwners: this.environment.pendingHeat,
      thermalFuelScans: this.environment.fuelScans,
      thermalFuelCellsVisited: this.environment.fuelCellsVisited,
      damageOldestMs: this.physics.damageInbox.oldestMs,
      captureJobs: this.physics.captureJobs,
      captureCells: this.physics.captureCells,
      captureOldestMs: this.physics.captureAgeMs,
      ...Object.fromEntries(
        Object.entries(this.physics.rebuildProfile).map(([key, value]) => [`rebuildMax_${key}`, value]),
      ),
      settledTransitions: this.physics.settledTransitions,
      rubbleReactivations: this.physics.reactivations,
      settledClusters: [...this.physics.clusters.values()].filter((c) => c.settled).length,
      phase: this.life.phase,
      mode: 'god',
      tool: this.tools.selected,
      buildings: this.city.buildings.length,
      props: this.city.props?.length ?? 0,
      bridges: this.city.bridges?.length ?? 0,
      liveCells: this.physics.liveCells,
      initialCells: this.physics.initialCells,
      removedCells: this.physics.removed,
      damagedCells: this.physics.damagedCells,
      drawCalls: this.view.renderer.info.render.calls,
      triangles: this.view.renderer.info.render.triangles,
      awakeBodies: this.physics.awake,
      bodies: this.physics.world.bodies.len(),
      colliders: this.physics.world.colliders.len(),
      clusters: this.physics.clusters.size,
      dormant: this.physics.dormant.size,
      renderedDormant: this.physics.renderedDormant.size,
      activeChunks: new Set([...this.physics.clusters.values()].map((c) => c.building.chunk)).size,
      jobs: this.physics.pendingWork,
      particles: this.effects.particles.length,
      audioVoices: this.audio.activeVoices,
      maxCatchUpSteps: this.clock.maxSteps,
      vehicles: this.population.cars.length,
      secondaryHits: this.physics.secondaryHits,
      structuralFailures: this.physics.structuralFailures,
      overloadedClusters: this.physics.stressed.size,
      assessmentSliceMs: this.physics.assessmentSliceMs,
      assessmentLatencyMs: this.physics.assessmentLatencyMs,
      charges: this.tools.charges.length,
      chargeCapacity: LIMITS.charges,
      c4Radius: this.tools.c4Radius,
      c4Footprint: this.settings.c4Footprint,
      nearbyDetailDistance: this.settings.viewDistance,
      sceneryDistance: this.settings.sceneryDistance,
      c4Strength: this.settings.c4Strength,
      registeredEntities: this.entities.size,
      workers: this.rebuild.workers.count,
      epoch: this.life.epoch,
      resets: this.resetCount,
      geometryResources: this.view.renderer.info.memory.geometries,
      textures: this.view.renderer.info.memory.textures,
      renderFactor: this.settings.adaptive ? this.adaptive.factor : 1,
      quality: this.settings.quality,
      renderCadence: 'display refresh (uncapped)',
      edition: EDITION,
      renderScale: this.settings.scale,
      adaptive: this.settings.adaptive ? 1 : 0,
    };
  }
  private readonlyMetrics() {
    return {
      snapshot: () => this.snapshot(),
      reconstruction: () => ({
        ...this.physics.reconstruction,
        profile: { ...this.rebuild.profile },
        work: this.restorationWork.map((entry) => ({ ...entry })),
        districts: this.physics.reconstruction?.districts.map((d) => ({ ...d })),
      }),
      timingSamples: () => this.metrics.timings.map((s) => ({ ...s })),
      stepSamples: () => this.physics.stepTimings.map((s) => ({ ...s })),
      contactAudit: () => this.physics.contactAudit(),
      // Read-only capture for isolated solver replay. This is an explicit
      // diagnostic outside frame-time windows, never a no-render GPU result.
      physicsCapture: () => {
        const bytes = this.physics.world.takeSnapshot(),
          parts: string[] = [];
        for (let i = 0; i < bytes.length; i += 16384)
          parts.push(String.fromCharCode(...bytes.subarray(i, i + 16384)));
        return { base64: btoa(parts.join('')), simulationSeconds: this.physics.time };
      },
      gpu: () => {
        const gl = this.view.renderer.getContext(),
          ext = gl.getExtension('WEBGL_debug_renderer_info');
        return {
          vendor: ext ? gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR),
          renderer: ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER),
          gpuTimerAvailable: !!gl.getExtension('EXT_disjoint_timer_query_webgl2'),
          multiDrawAvailable: !!gl.getExtension('WEBGL_multi_draw'),
        };
      },
      project: (x: number, y: number, z: number) => {
        const p = new THREE.Vector3(x, y, z).project(this.view.camera);
        return {
          x: ((p.x + 1) / 2) * innerWidth,
          y: ((1 - p.y) / 2) * innerHeight,
          visible: p.z >= -1 && p.z <= 1,
        };
      },
      districts: () => this.city.districts,
      incidents: () => this.fire.incidents(),
      actors: () => this.population.actors(),
      audio: () => this.audio.inspect(),
      activities: (id?: string) => this.population.civil?.inspect(id),
      services: () => this.population.services?.inspect(),
      rail: () => this.population.rail?.inspect(),
      buses: () => this.population.buses?.inspect(),
      deliveries: () => this.population.logistics?.inspect(),
      shopStaff: () => this.population.shopStaff?.inspect(),
      coast: () => ({ layout: this.city.coast, staff: this.population.coastStaff?.inspect() }),
      strategic: () => ({ layout: this.city.strategic, plant: this.population.powerPlant?.inspect() }),
      military: () => this.population.military?.inspect(),
      naval: () => this.population.naval?.inspect(),
      invasion: () => this.population.invasion?.inspect(),
      maritime: () => this.population.military?.maritime.inspect(),
      shelters: () => this.population.shelters?.inspect(),
      drivers: () => this.population.drivers?.inspect(),
      marine: () => this.population.marine?.inspect(),
      aviation: () => this.population.aviation?.inspect(),
      airSupport: () => this.population.airSupport?.inspect(),
      interactions: () => this.population.civil?.interactions.map((e) => ({ ...e })),
      interiors: () => this.city.interiors,
      places: () =>
        this.city.places?.map((p) => ({
          ...p,
          available: this.population.civil?.network.available(p),
          stock: this.population.civil?.inventory.get(p.id),
        })),
      attachments: () =>
        this.tools.charges.map((c) => ({
          id: c.id,
          cellId: c.cellId,
          target: c.attachment?.target,
          fixed: c.fixed,
          loose: !!c.looseBody,
          epoch: c.epoch,
          point: c.mesh.position.toArray(),
        })),
      structures: (buildingId?: number) => [
        ...[...this.physics.clusters.values()]
          .filter((c) => buildingId === undefined || c.building.id === buildingId)
          .map((c) => ({
            id: c.id,
            buildingId: c.building.id,
            cells: c.cells.size,
            anchored: c.anchored,
            position: { ...c.body.translation() },
            rotation: { ...c.body.rotation() },
            originalPosition: {
              x: c.building.x + c.origin.x,
              y: (c.building.baseY ?? 0.16) + c.origin.y,
              z: c.building.z + c.origin.z,
            },
          })),
        ...[...this.physics.dormant.values()]
          .filter((c) => buildingId === undefined || c.building.id === buildingId)
          .map((c) => ({
            id: c.id,
            buildingId: c.building.id,
            cells: c.cells.size,
            anchored: c.anchored,
            position: { ...c.position },
            rotation: { ...c.rotation },
            originalPosition: {
              x: c.building.x + c.origin.x,
              y: (c.building.baseY ?? 0.16) + c.origin.y,
              z: c.building.z + c.origin.z,
            },
          })),
      ],
      targets: () => [
        ...this.physics.buildings.map((b) => ({
          id: b.id,
          name: b.name,
          category: b.category ?? 'building',
          kind: b.kind,
          originalCells: b.cells.length,
          minX: b.x,
          minZ: b.z,
          w: b.w,
          d: b.d,
          baseY: b.baseY ?? 0.16,
          x: b.x + b.w * 0.625,
          y: (b.baseY ?? 0.16) + b.h * 1.25,
          z: b.z + b.d * 0.625,
          // Runtime airframes retain an authored local origin while flying.
          // Diagnostics must frame a live body cell rather than that old origin.
          ...(b.kind === 'aircraft'
            ? (this.physics.cellPosition(
                (
                  this.physics.buildingCells(b.id).find((c) => c.x === 13 && c.y === 2) ??
                  this.physics.buildingCells(b.id)[0]
                )?.id ?? -1,
              ) ?? {})
            : {}),
        })),
        ...this.entities.targets(),
      ],
      targetRay: (x: number, y: number, z: number) => {
        const direction = new THREE.Vector3(x, y, z).sub(this.view.camera.position).normalize(),
          hit = this.physics.ray(this.view.camera.position, direction);
        return hit
          ? {
              buildingId: hit.cluster?.building.id ?? hit.dormant?.building.id,
              owner: hit.cluster?.id ?? hit.dormant?.id,
              anchored: hit.cluster?.anchored ?? hit.dormant?.anchored,
              body: hit.collider?.parent()?.handle,
              entity: hit.entity,
              point: hit.point.toArray(),
            }
          : null;
      },
      position: () => ({
        camera: this.view.camera.position.toArray(),
        target: this.cameras.target.toArray(),
        distance: this.cameras.distance,
        yaw: this.cameras.yaw,
        pitch: this.cameras.pitch,
      }),
    };
  }
  debug(): void {
    Object.assign(window, {
      __sandbox: {
        ...this.readonlyMetrics(),
        resetMetrics: () => this.metrics.reset(),
        buildings: () =>
          this.physics.buildings.map((b) => ({
            id: b.id,
            kind: b.kind,
            x: b.x,
            z: b.z,
            w: b.w,
            d: b.d,
            h: b.h,
            category: b.category,
          })),
        damage: (id: number, x: number, y: number, z: number, radius: number, energy: number) => {
          for (const c of this.physics.activateBuilding(id)) this.physics.hit(c, { x, y, z }, radius, energy);
        },
        demolishAll: () => {
          for (const b of this.physics.buildings)
            for (const c of this.physics.activateBuilding(b.id))
              this.physics.hit(c, c.body.translation(), 900, 500);
        },
        explode: (x: number, y: number, z: number, radius = 6, energy = 7) =>
          this.physics.blast({ x, y, z }, radius, energy),
        focus: (x: number, z: number, distance = 135) => {
          this.cameras.target.set(x, 3, z);
          this.cameras.distance = distance;
          this.physics.stream(this.cameras.target, Math.max(100, distance));
        },
        cells: (id: number) => this.physics.buildingCells(id).map((c) => ({ ...c, ...cellCenter(c) })),
        structural: () =>
          [...this.physics.clusters.values()].map((c) => ({
            id: c.id,
            buildingId: c.building.id,
            anchored: c.anchored,
            cells: c.cells.size,
            maxRatio: c.stress?.maxRatio ?? 0,
            position: { ...c.body.translation() },
            rotation: { ...c.body.rotation() },
          })),
        charges: () =>
          this.tools.charges.map((c) => ({
            id: c.id,
            cellId: c.cellId,
            body: c.targetBody?.handle,
            point: c.mesh.position.toArray(),
          })),
        ray: (x: number, y: number, z: number, dx: number, dy: number, dz: number) => {
          const h = this.physics.ray({ x, y, z }, { x: dx, y: dy, z: dz });
          return h ? { point: h.point.toArray(), cluster: h.cluster?.id } : null;
        },
        obstructed: (x: number, z: number) => this.physics.blocked(x, z),
      },
    });
  }
}
async function start(): Promise<void> {
  try {
    await RAPIER.init();
    new Game();
  } catch (error) {
    console.error(error);
    const el = document.getElementById('loading');
    if (el)
      el.textContent = `The city could not start. WebGL2 and WebAssembly SIMD are required. ${error instanceof Error ? error.message : String(error)}`;
  }
}
void start();
